import i18n from 'i18next';

import {resize} from '@pexip/media-processor';
import type {ImageRecord as BitmapRecord} from '@pexip/media-processor';
import {notificationToastSignal} from '@pexip/components';

import {
    PROCESSING_HEIGHT,
    PROCESSING_WIDTH,
    IMAGE_DATABASE_NAME,
    IMAGE_DATABASE_VERSION,
    IMAGE_OBJECTSTORE_NAME,
    IMAGE_OBJECTSTORE_KEY_PATH,
    IMAGE_OBJECTSTORE_KEY_NAME,
    USER_CUSTOM_IMAGE_KEY,
} from '../constants';
import type {ImageRecord} from '../types';
import {logger} from '../logger';
import {userCustomImageSignal} from '../signals/ImageStore.signals';
import {toImageBitmapRecord} from '../utils/toBitmapRecord';
import {openDatabase, beginTransaction, idbRequest} from '../utils/indexedDB';

type Close = () => void;

interface Props {
    imageUrl: string[];
    bitmapRecord?: BitmapRecord;
    imageRecord?: ImageRecord;
}

const createCanvas = (width: number, height: number) => {
    if ('OffscreenCanvas' in self) {
        return new OffscreenCanvas(width, height);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
};

/**
 * Concert canvas to Blob with type set to image/png for maximum compatibility.
 */
const createBlob = (
    canvas: HTMLCanvasElement | OffscreenCanvas,
    quality: number,
) => {
    if (canvas instanceof HTMLCanvasElement) {
        return new Promise<Blob | null>(resolve => {
            canvas.toBlob(resolve, 'image/png', quality);
        });
    }
    return canvas.convertToBlob({quality, type: 'image/png'});
};

/**
 * Create an image store to manage the user custom image.
 */
export const createImageStore = () => {
    const internal = {
        imageMap: new Map<string, ImageRecord>(),
        imageUrl: [] as string[],

        canvas: createCanvas(PROCESSING_WIDTH, PROCESSING_HEIGHT),
    };
    const props: Props = {
        get imageUrl() {
            return internal.imageUrl;
        },
    };

    /**
     * Update the internal state with the new image record.
     * @param record - The new image record.
     */
    const updateInternal = async (record: ImageRecord) => {
        props.imageRecord = record;
        // Clean up the old image
        for (const url of internal.imageUrl) {
            URL.revokeObjectURL(url);
            internal.imageMap.delete(url);
        }
        const url = URL.createObjectURL(props.imageRecord.data);
        internal.imageUrl = [url];
        internal.imageMap.set(url, props.imageRecord);

        const bitmapRecord = await toImageBitmapRecord(record);
        props.bitmapRecord?.image.close();
        props.bitmapRecord = bitmapRecord;

        // Notify the subscribers
        userCustomImageSignal.emit(record);
    };

    /**
     * Clear the internal state.
     */
    const clearInternal = () => {
        props.imageRecord = undefined;
        internal.imageMap.clear();
        internal.imageUrl = [];
        props.bitmapRecord = undefined;

        // Notify the subscribers
        userCustomImageSignal.emit(undefined);
    };

    /**
     * Handle the upgrade event by creating the object store.
     * @param db - The database to upgrade.
     * @param event - The upgrade event.
     */
    const upgradeImageStore = (
        db: IDBDatabase,
        event: IDBVersionChangeEvent,
    ) => {
        logger.info({event, db}, 'Handle upgradeneeded event');
        const objectStore = db.createObjectStore(IMAGE_OBJECTSTORE_NAME, {
            keyPath: 'key',
        });
        objectStore.createIndex(
            IMAGE_OBJECTSTORE_KEY_NAME,
            IMAGE_OBJECTSTORE_KEY_PATH,
            {unique: true},
        );
    };
    /**
     * Write the user custom image in the database.
     * @param name - The name of the image.
     * @param data - The image data.
     */
    const write = async (name: string, data: Blob) => {
        let closeDB: Close | undefined;
        try {
            const {db, close} = await openDatabase(
                IMAGE_DATABASE_NAME,
                IMAGE_DATABASE_VERSION,
                upgradeImageStore,
            );
            closeDB = close;
            const {transaction, endTransaction} = beginTransaction(
                db,
                [IMAGE_OBJECTSTORE_NAME],
                'readwrite',
            );
            const objectStore = transaction.objectStore(IMAGE_OBJECTSTORE_NAME);
            await idbRequest(
                objectStore.put({
                    key: USER_CUSTOM_IMAGE_KEY,
                    name,
                    data,
                }),
                `Write image[${name}] to DB.`,
            );
            endTransaction();
            closeDB();
        } catch (error) {
            logger.error(
                {error, data},
                `Error occurred while writing image[${name}] to DB`,
            );
            closeDB?.();
        }
    };
    /**
     * Retrieve the user custom image from the database.
     * @returns The user custom image record.
     */
    const retrieve = async () => {
        let closeDB: Close | undefined;
        try {
            const {db, close} = await openDatabase(
                IMAGE_DATABASE_NAME,
                IMAGE_DATABASE_VERSION,
                upgradeImageStore,
            );
            closeDB = close;
            const {transaction, endTransaction} = beginTransaction(
                db,
                [IMAGE_OBJECTSTORE_NAME],
                'readonly',
            );
            const objectStore = transaction.objectStore(IMAGE_OBJECTSTORE_NAME);
            const record = await idbRequest<ImageRecord | undefined>(
                objectStore.get(USER_CUSTOM_IMAGE_KEY),
                'Get user custom image.',
            );
            endTransaction();
            if (record) {
                await updateInternal(record);
            }
            closeDB();
            return record;
        } catch (error) {
            logger.error(
                {error},
                'Error occurred while reading user custom image',
            );
            closeDB?.();
        }
    };

    /**
     * Load and optimize the image.
     * @param file - The image file to load.
     * @param quality - The quality of the image to be optimized.
     * @throws Error - If an error occurs while loading and optimizing the image.
     */
    const loadAndOptimizeImage = async (file: File, quality = 0.8) => {
        // Load directly as an ImageBitmap so we can load into Canvas smoothly
        const bitmap = await createImageBitmap(file);
        logger.info(
            {image: bitmap},
            `Image Loaded: ${file.name} ${bitmap.width}x${bitmap.height}`,
        );
        const ctx = internal.canvas.getContext('2d', {
            alpha: false,
        }) as CanvasRenderingContext2D;
        // The canvas is reused, so previous pixels would show through a transparent image.
        // Clear it before drawing the new image.
        ctx?.clearRect(0, 0, internal.canvas.width, internal.canvas.height);
        // Size optimization: Resize the image to our desired size
        const rects = resize(
            bitmap.width,
            bitmap.height,
            PROCESSING_WIDTH,
            PROCESSING_HEIGHT,
        );
        // Resize Image to our desired size
        ctx?.drawImage(
            bitmap,
            rects.sx,
            rects.sy,
            rects.sw,
            rects.sh,
            rects.dx,
            rects.dy,
            rects.dw,
            rects.dh,
        );
        // Convert the canvas to a Blob also optimize the image quality
        const blob = await createBlob(internal.canvas, quality);
        // Close the bitmap as it is no longer needed
        bitmap.close();
        if (!blob) {
            throw new Error('Unable to Blob from canvas');
        }
        return blob;
    };

    /**
     * Save the image to the database.
     * @param file - The image file to save.
     */
    const save = async (file: File) => {
        try {
            const blob = await loadAndOptimizeImage(file);
            await write(file.name, blob);
            await updateInternal({
                name: file.name,
                data: blob,
                key: USER_CUSTOM_IMAGE_KEY,
            });
        } catch (error) {
            logger.error(
                {error},
                `Error occurred while reading and saving image ${file.name}`,
            );

            notificationToastSignal.emit([
                {
                    message: i18n.t(
                        'media.upload-failed',
                        'File upload failed',
                    ),
                },
            ]);
        }
    };

    const clearDb = async () => {
        let closeDB: Close | undefined;
        try {
            const {db, close} = await openDatabase(
                IMAGE_DATABASE_NAME,
                IMAGE_DATABASE_VERSION,
                upgradeImageStore,
            );
            closeDB = close;
            const {transaction, endTransaction} = beginTransaction(
                db,
                [IMAGE_OBJECTSTORE_NAME],
                'readwrite',
            );
            const objectStore = transaction.objectStore(IMAGE_OBJECTSTORE_NAME);
            const result = await idbRequest(
                objectStore.delete(USER_CUSTOM_IMAGE_KEY),
                `Delete ${USER_CUSTOM_IMAGE_KEY} record`,
            );

            endTransaction();
            closeDB();
            return result;
        } catch (error) {
            logger.error(
                {error},
                'Error occurred while clearing user custom image',
            );
            closeDB?.();
        }
    };

    const clear = async () => {
        try {
            await clearDb();
            clearInternal();
        } catch (error) {
            logger.error({error}, 'Error occurred while clearing the storage');
        }
    };
    return {
        /**
         * Read the user custom image from the Image Store.
         */
        read: () => {
            void retrieve();
        },
        /**
         * Save the user custom image to the Image Store.
         */
        save: (file: File) => {
            void save(file);
        },
        /**
         * Get the ImageRecord with the provided url.
         */
        resolveUserCustomImageUrl: (url?: string) => {
            if (!url) {
                return undefined;
            }
            return internal.imageMap.get(url);
        },
        /**
         * Get the current BitmapRecord.
         */
        getBitmapRecord: () => {
            return props.bitmapRecord;
        },
        /**
         * Get the current url.
         */
        getUserCustomImageUrl: () => {
            return props.imageUrl;
        },
        /**
         * Clear the user custom image.
         */
        clear: () => {
            void clear();
        },
    };
};

export const imageStore = createImageStore();

export type ImageStore = ReturnType<typeof createImageStore>;
